import { useMemo } from 'react';
import { Volume } from '@paradigm/features/src/leaderboard/entities/volume';
import { useVolumesListState } from '@paradigm/features/src/leaderboard/contexts/volumes-list';
import {
  EmptyCell,
  FirmCell,
  HeaderCell,
  PercentageCell,
  RankCell,
  BigNumberCell,
} from '#/components/Leaderboard/shared/leaderboard-table/cells';
import { LeaderboardColumn } from '#/components/Leaderboard/shared/leaderboard-table/LeaderboardTable';
import { bigNumberSortBy } from '#/components/Leaderboard/shared/leaderboard-table/sort';

export default function useColumns() {
  const { mobileView } = useVolumesListState();

  const columns = useMemo<LeaderboardColumn<Volume>[]>(
    () => [
      {
        id: 'rank',
        header: ({ column }) => <HeaderCell column={column} title="Rank" />,
        cell: ({ cell }) => (
          <RankCell isUserRow={cell.row.original.isFromUser} />
        ),
        enableResizing: false,
        enableSorting: false,
        meta: { isMobileHeader: true, draggable: false },
      },
      {
        id: 'firm',
        accessorKey: 'firmName',
        header: ({ column }) => <HeaderCell column={column} title="Firm" />,
        cell: FirmCell,
        enableResizing: false,
        enableSorting: false,
        meta: { isMobileHeader: true, draggable: false },
      },
      {
        id: 'spacer',
        accessorKey: 'spacer',
        header: EmptyCell,
        cell: EmptyCell,
        enableResizing: false,
        enableSorting: false,
        meta: { isMobileHeader: true, draggable: false },
      },
      {
        id: 'maker_share',
        header: ({ column }) => <HeaderCell column={column} title="Share" />,
        accessorKey: 'makerShare',
        enableResizing: false,
        cell: PercentageCell,
        sortingFn: bigNumberSortBy('makerShare'),
        meta: { isMobileHeader: mobileView === 'Maker', draggable: false },
        sortDescFirst: true,
        sortingDirection: 'desc',
      },
      {
        id: 'maker_volume',
        header: ({ column }) => (
          <HeaderCell column={column} title="Maker Volume" />
        ),
        accessorKey: 'makerVolume',
        enableResizing: false,
        cell: BigNumberCell,
        sortingFn: bigNumberSortBy('makerVolume'),
        meta: { isMobileHeader: mobileView === 'Maker', draggable: false },
        sortDescFirst: true,
        sortingDirection: 'desc',
      },
      {
        id: 'taker_share',
        header: ({ column }) => <HeaderCell column={column} title="Share" />,
        accessorKey: 'takerShare',
        enableResizing: false,
        cell: PercentageCell,
        sortingFn: bigNumberSortBy('takerShare'),
        meta: { isMobileHeader: mobileView === 'Taker', draggable: false },
        sortDescFirst: true,
        sortingDirection: 'desc',
      },
      {
        id: 'taker_volume',
        header: ({ column }) => (
          <HeaderCell column={column} title="Taker Volume" />
        ),
        accessorKey: 'takerVolume',
        enableResizing: false,
        cell: BigNumberCell,
        sortingFn: bigNumberSortBy('takerVolume'),
        meta: { isMobileHeader: mobileView === 'Taker', draggable: false },
        sortDescFirst: true,
        sortingDirection: 'desc',
      },
      {
        id: 'total_share',
        accessorKey: 'totalShare',
        header: ({ column }) => <HeaderCell column={column} title="Share" />,
        sortingFn: bigNumberSortBy('totalShare'),
        sortDescFirst: true,
        sortingDirection: 'desc',
        enableResizing: false,
        cell: PercentageCell,
        meta: { isMobileHeader: mobileView === 'Overall', draggable: false },
      },
      {
        id: 'total_volume',
        accessorKey: 'totalVolume',
        sortingFn: bigNumberSortBy('totalVolume'),
        sortDescFirst: true,
        sortingDirection: 'desc',
        header: ({ column }) => (
          <HeaderCell column={column} title="Total Volume" />
        ),
        cell: BigNumberCell,
        enableResizing: false,
        isDefaultSorting: true,
        meta: {
          isMobileHeader: mobileView === 'Overall',
          draggable: false,
        },
      },
    ],
    [mobileView],
  );

  return { columns };
}
